/**
 * DEFUSE S15 Slice F — ENTITY-LEVEL viewport transforms (the ONE TRUTH the
 * dedicated `geometry/viewport-*` commands AND the Properties-panel side-effect
 * mutators BOTH call).
 *
 * The seven per-viewport commands live in `viewportCommands.ts` and operate on a
 * `DrawingDocument` (find the viewport by id, replace it, and — for the clip
 * commands — detach OBJECTS chains). Their per-viewport ENTITY transform, however,
 * is pure entity→entity: `applyViewportSetScale` / `applyViewportSetView` /
 * `applyViewportSetLock` clone the target viewport and rebuild its COUPLED fields
 * (scale ↔ view-height ↔ vpOverrideScale ↔ VP_OVERRIDES XDATA ↔ cannoscale, or the
 * lock bit ↔ displayLocked ↔ XDATA). This module LIFTS that entity transform out so
 * that:
 *   - the doc-level command delegates to it (find + guard + delegate + replace), and
 *   - the S15 Slice F Properties-palette side-effect mutators
 *     (`viewportMutator` / `viewportScaleMutator` in `sideEffectMutators.ts`) call
 *     the SAME function.
 * So a Scale / View / Display-lock edit typed into the Properties panel produces the
 * IDENTICAL document state as the dedicated command — one truth, no hand-rolled
 * reconcile (ПРИОРИТЕТ #1). The command's extensive undo / multi-carrier-scale
 * regression coverage (`viewportSetViewScaleUndo.test.ts`, `viewportCommands.test.ts`)
 * therefore ALSO covers the panel path by construction.
 *
 * PURE by construction: each helper returns a NEW entity on a real change and the
 * SAME reference on a no-op (the paper-space control viewport `viewportId === 1`, or
 * `applyViewportSetView`'s unchanged-values case), so a doc-level caller derives
 * `mutated` from `updated !== vp` and the side-effect diff derives an empty patch.
 * `writeViewportOverrides` mutates only the fresh clone (never the input).
 *
 * PLACEMENT (ПРИОРИТЕТ #2): LEAF module — imports ONLY `@dxf-ai-editor/drawing-model`
 * (no back-edge to `viewportCommands` / `sideEffectMutators` / `descriptors`), so the
 * `viewportCommands → viewportMutateEntity` and `sideEffectMutators →
 * viewportMutateEntity` and `descriptors/containers → viewportMutateEntity` edges are
 * all acyclic.
 *
 * CLIP (EPIC-VIEWPORT-CLIP): the `recomputeNativeClipPath` re-projection is retained
 * VERBATIM from the commands so panel + command stay byte-identical. It is NOT a clip
 * WRITE path (it never authors / edits a clip boundary — clip / membership stay
 * reader-only side-channels): it only re-projects the DERIVED model-space `clipPath`
 * of an ALREADY-clipped viewport through the new view transform, and returns null
 * (no-op) for the common unclipped viewport (no `clipBoundaryPaperSpace`).
 */

import {
  readViewportOverrides,
  recomputeNativeClipPath,
  writeViewportOverrides,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

/**
 * Parse a `num:den` viewport-scale label into its rational parts (S15 Slice F —
 * the ONE regex the descriptor `Scale` toRaw AND the `viewportScaleMutator` share).
 * The mutator needs BOTH parts (`applyViewportSetScale` stores `vpOverrideScale`
 * {num,denom} + the `num:denom` cannoscale name), which the lossy `num/den` ratio
 * cannot reconstruct — so the mutator parses the raw label here, not the ratio.
 *
 * VALIDATION LIFT (one-truth-INCLUDING-validation): both parts MUST be
 * STRICTLY POSITIVE — the dedicated `geometry/viewport-set-scale` command enforces
 * `scaleNum` / `scaleDenom` are `z.number().positive()` (editor.ts) BEFORE
 * `applyViewportSetScale` runs, but the panel side-effect path reaches
 * `setViewportScaleEntity` WITHOUT that schema, so this parser is the equivalent
 * gate. A `0:N` label would otherwise compute `viewHeight = height × (N/0) =
 * Infinity` (and a non-finite viewHeight has NO schema guard — model.ts — so it
 * would persist into render + DXF export). The regex already rejects a leading
 * minus, so `num > 0 && denom > 0` closes the remaining `0`-numerator/denominator
 * hole; a bad label (unparseable / zero / non-finite) → null → the edit no-ops,
 * EXACTLY as the command schema rejects it.
 */
export function parseViewportScaleFraction(
  s: string,
): { num: number; denom: number } | null {
  const m = s.trim().match(/^([0-9]*\.?[0-9]+)\s*:\s*([0-9]*\.?[0-9]+)$/);
  if (!m) return null;
  const num = Number(m[1]);
  const denom = Number(m[2]);
  if (
    !Number.isFinite(num) ||
    !Number.isFinite(denom) ||
    !(num > 0) ||
    !(denom > 0)
  ) {
    return null;
  }
  return { num, denom };
}

/**
 * The paper-space CONTROL viewport (DXF viewportId=1, the layout's overview
 * pseudo-viewport) is NEVER user-modifiable — render / print / the dedicated
 * viewport commands all treat it as inert. ONE detection rule the ENTITY mutators
 * (scale / lock / view) AND the plain typed-writer guard
 * (`buildTypedExtendedGeometryEdit`) share, so no site can drift from another
 * (@codex round-6 P2-2 — the plain leaves must skip the control VP exactly as the
 * side-effect mutators already do).
 */
export function isControlViewport(vp: ViewportEntity): boolean {
  return vp.viewportId === 1;
}

/**
 * The EXACT stored scale rational a viewport carries — the AUTHORITATIVE
 * `vpOverrideScale` typed field (what the mutator writes + the DXF export reads),
 * else the `viewportCannoscaleName` label parsed back to its parts — or `null`
 * when the viewport has NO exact carrier (a purely derived-ratio scale).
 *
 * @codex round-6 P2-1 — the Scale directive's CHANGED-VALUE no-op guard compares a
 * submitted `num:denom` label against THIS, not the lossy derived display: `2:200`
 * and `1:100` share a ratio (both display "1:100") but are DISTINCT carriers, so a
 * relabel from the stored `2:200` to `1:100` MUST rewrite (not be dropped as a
 * display-equal no-op). Positivity-gated (the mutator only writes positive carriers)
 * so a degenerate stored value falls back to the cannoscale/display comparison.
 */
export function viewportStoredScaleFraction(
  vp: ViewportEntity,
): { num: number; denom: number } | null {
  const override = vp.vpOverrideScale;
  if (
    override &&
    Number.isFinite(override.num) &&
    Number.isFinite(override.denom) &&
    override.num > 0 &&
    override.denom > 0
  ) {
    return { num: override.num, denom: override.denom };
  }
  if (vp.viewportCannoscaleName) {
    return parseViewportScaleFraction(vp.viewportCannoscaleName);
  }
  return null;
}

/**
 * The entity transform of `applyViewportSetScale` (MVSETUP scale) — clone the
 * viewport with the requested `num:denom` scale, deriving `viewHeight = paperHeight
 * × (denom / num)` (group 41 → group 45), the `num:denom` cannoscale name +
 * `num/denom` factor, the authoritative `vpOverrideScale` typed field, and the
 * VP_OVERRIDES XDATA scale codes (DXF round-trip). Re-projects a native clip path.
 * Returns `vp` UNCHANGED for the paper-space control viewport (never user-editable).
 */
export function setViewportScaleEntity(
  vp: ViewportEntity,
  scaleNum: number,
  scaleDenom: number,
): ViewportEntity {
  // Paper-space control viewport (DXF viewportId=1) must not be user-modifiable.
  if (isControlViewport(vp)) return vp;

  const overrides = readViewportOverrides(vp);
  overrides.scale = { num: scaleNum, denom: scaleDenom };

  // Derive viewHeight from scale: viewHeight = paper-height × (denom / num).
  const newViewHeight = vp.height * (scaleDenom / scaleNum);
  const cannoscaleName = `${scaleNum}:${scaleDenom}`;
  const cannoscaleFactor = scaleNum / scaleDenom;

  const updated: ViewportEntity = {
    ...vp,
    viewHeight: newViewHeight,
    viewportCannoscaleName: cannoscaleName,
    viewportCannoscaleFactor: cannoscaleFactor,
    // authoritative scale on the typed field so DXF export uses it instead of
    // re-deriving from viewHeight/height floats (which can lose precision).
    vpOverrideScale: { num: scaleNum, denom: scaleDenom },
  };
  writeViewportOverrides(overrides, updated);

  // A native group-340 VPCLIP boundary is FIXED on the paper sheet; the scale
  // change changes viewHeight (the paper→model view transform), so re-project the
  // DERIVED model-space clipPath from the retained paper-space boundary. null for a
  // SPATIAL_FILTER clip (fixed in model space) OR an unclipped viewport — leave it.
  const recomputedScaleClip = recomputeNativeClipPath(updated);
  if (recomputedScaleClip) updated.clipPath = recomputedScaleClip;

  return updated;
}

/**
 * The entity transform of `applyViewportSetLock` — clone the viewport with
 * `displayLocked` + the VP_OVERRIDES XDATA lock flag + the group-90 status-bit
 * 0x4000 kept in sync (DXF import derives `displayLocked` FROM that bit, so without
 * it the lock is lost on round-trip). Returns `vp` UNCHANGED for the control VP.
 */
export function setViewportLockEntity(
  vp: ViewportEntity,
  locked: boolean,
): ViewportEntity {
  // Paper-space control viewport (DXF viewportId=1) must not be user-modifiable.
  if (isControlViewport(vp)) return vp;

  const overrides = readViewportOverrides(vp);
  overrides.locked = locked;

  const updated: ViewportEntity = { ...vp };
  writeViewportOverrides(overrides, updated);

  // Keep group-90 statusBitFlags bit 0x4000 in sync with the lock state (import
  // derives displayLocked FROM this bit). Preserve every other bit.
  const lockBit = 0x4000;
  const baseBits = vp.statusBitFlags ?? 0;
  updated.statusBitFlags = locked ? baseBits | lockBit : baseBits & ~lockBit;

  return updated;
}

/**
 * The pan/zoom (view-center + view-height) mutation of `applyViewportSetView`. See
 * the command doc for the full contract: the FORWARD (panel / user / AI) path
 * re-derives the displayed scale when viewHeight changes; the UNDO path threads the
 * TWO independent scale carriers (`restoreVpOverrideScale` typed field +
 * `restoreXdataScale` VP_OVERRIDES XDATA) and restores each verbatim (@codex #419).
 * The panel side-effect mutator uses ONLY the forward fields ({viewCenter} pan /
 * {viewHeight} zoom); the restore fields are the command's computed inverse.
 */
export type ViewportViewMutation = {
  viewCenter?: { x: number; y: number };
  viewHeight?: number;
  restoreVpOverrideScale?: { num: number; denom: number } | null;
  restoreXdataScale?: { num: number; denom: number } | null;
};

/**
 * The entity transform of `applyViewportSetView` — persist a viewport's pan/zoom
 * view state (viewCenter group 12/22, viewHeight group 45), re-deriving the
 * displayed scale on a zoom (or restoring the snapshotted carriers on undo).
 * Returns `vp` UNCHANGED when nothing is supplied, the supplied values match the
 * current state, or the target is the control viewport (all no-op cases).
 */
export function setViewportViewEntity(
  vp: ViewportEntity,
  view: ViewportViewMutation,
): ViewportEntity {
  // Paper-space control viewport (DXF viewportId=1) must not be user-modifiable.
  if (isControlViewport(vp)) return vp;

  const nextViewCenter = view.viewCenter ?? vp.viewCenter;
  const nextViewHeight = view.viewHeight ?? vp.viewHeight;

  // No-op detection: nothing supplied, or supplied values match current state.
  const centerUnchanged =
    view.viewCenter === undefined ||
    (nextViewCenter.x === vp.viewCenter.x && nextViewCenter.y === vp.viewCenter.y);
  const heightUnchanged =
    view.viewHeight === undefined || nextViewHeight === vp.viewHeight;
  if (centerUnchanged && heightUnchanged) return vp;

  const updated: ViewportEntity = {
    ...vp,
    viewCenter: { x: nextViewCenter.x, y: nextViewCenter.y },
    viewHeight: nextViewHeight,
  };

  // UNDO-RESTORE (preserve-source) — TWO INDEPENDENT CARRIERS (@codex #419). See the
  // command doc: restore each carrier to its own pre-mutation snapshot, never
  // inferring one from the other, and SKIP the lossy forward re-derive.
  if (view.restoreVpOverrideScale !== undefined) {
    if (view.restoreVpOverrideScale === null) {
      delete updated.vpOverrideScale;
    } else {
      updated.vpOverrideScale = {
        num: view.restoreVpOverrideScale.num,
        denom: view.restoreVpOverrideScale.denom,
      };
    }

    if (view.restoreXdataScale !== undefined) {
      const overrides = readViewportOverrides(vp);
      if (view.restoreXdataScale === null) {
        if (overrides.scaleFromXdata !== undefined) {
          writeViewportOverrides(overrides, updated, { omitScale: true });
        }
      } else {
        overrides.scale = {
          num: view.restoreXdataScale.num,
          denom: view.restoreXdataScale.denom,
        };
        writeViewportOverrides(overrides, updated);
      }
    }
  } else if (view.viewHeight !== undefined && vp.height > 0 && nextViewHeight > 0) {
    // Forward zoom — re-derive the displayed scale so the status-bar selector
    // reflects the new magnification (paperHeight = vp.height, group 41).
    const denom = Math.round(nextViewHeight / vp.height);
    const safeDenom = denom > 0 ? denom : 1;
    updated.vpOverrideScale = { num: 1, denom: safeDenom };

    const overrides = readViewportOverrides(vp);
    overrides.scale = { num: 1, denom: safeDenom };
    writeViewportOverrides(overrides, updated);
  }

  // Re-project a native group-340 clip path through the new view transform (fixed
  // on paper; null for a SPATIAL_FILTER / unclipped viewport — leave it).
  const recomputedViewClip = recomputeNativeClipPath(updated);
  if (recomputedViewClip) updated.clipPath = recomputedViewClip;

  return updated;
}
